export type EnvironmentThemeId = 'park_model' | 'toyworks' | 'afterglow';

export interface EnvironmentThemeDefinition {
  readonly id: EnvironmentThemeId;
  readonly label: string;
  readonly role: 'default' | 'optional';
  readonly colors: Readonly<{
    background: number;
    fog: number;
    plotTop: number;
    plotEdge: number;
    path: number;
    foliage: number;
    foliageAccent: number;
    trunk: number;
    stone: number;
    practical: number;
    skyTop: number;
    skyHorizon: number;
  }>;
  readonly surface: Readonly<{
    groundRoughness: number;
    sceneryRoughness: number;
    stoneRoughness: number;
  }>;
  readonly sky: Readonly<{
    turbidity: number;
    rayleigh: number;
    mieCoefficient: number;
    mieDirectionalG: number;
    elevationDegrees: number;
    azimuthDegrees: number;
  }>;
  readonly lighting: Readonly<{
    hemisphereSky: number;
    hemisphereGround: number;
    hemisphereIntensity: number;
    sunColor: number;
    sunIntensity: number;
    fillColor: number;
    fillIntensity: number;
    exposure: number;
  }>;
  readonly fog: Readonly<{ near: number; far: number }> | null;
  readonly scenery: Readonly<{
    treeRatio: number;
    hedgeRatio: number;
    lampRatio: number;
  }>;
  readonly effects: Readonly<{
    bloomCandidate: boolean;
    outlineCandidate: boolean;
  }>;
}

function freezeTheme(theme: EnvironmentThemeDefinition): EnvironmentThemeDefinition {
  Object.freeze(theme.colors);
  Object.freeze(theme.surface);
  Object.freeze(theme.sky);
  Object.freeze(theme.lighting);
  if (theme.fog) Object.freeze(theme.fog);
  Object.freeze(theme.scenery);
  Object.freeze(theme.effects);
  return Object.freeze(theme);
}

const parkModel = freezeTheme({
  id: 'park_model',
  label: 'Park Model',
  role: 'default',
  colors: {
    background: 0xb8d4d7,
    fog: 0xb8d4d7,
    plotTop: 0x71897d,
    plotEdge: 0x293c49,
    path: 0xa18f72,
    foliage: 0x2f684f,
    foliageAccent: 0x568964,
    trunk: 0x5b4236,
    stone: 0x747b79,
    practical: 0xffd897,
    skyTop: 0x72a7cb,
    skyHorizon: 0xcde8e4,
  },
  surface: { groundRoughness: 0.94, sceneryRoughness: 0.88, stoneRoughness: 0.82 },
  sky: {
    turbidity: 2.2,
    rayleigh: 1.25,
    mieCoefficient: 0.004,
    mieDirectionalG: 0.78,
    elevationDegrees: 39,
    azimuthDegrees: 128,
  },
  lighting: {
    hemisphereSky: 0xf2fbff,
    hemisphereGround: 0x314035,
    hemisphereIntensity: 0.52,
    sunColor: 0xfff2d6,
    sunIntensity: 2.75,
    fillColor: 0x9fc7db,
    fillIntensity: 0.16,
    exposure: 0.94,
  },
  fog: { near: 132, far: 285 },
  scenery: { treeRatio: 0.58, hedgeRatio: 0.32, lampRatio: 0.1 },
  effects: { bloomCandidate: false, outlineCandidate: true },
});

const toyworks = freezeTheme({
  id: 'toyworks',
  label: 'Toyworks',
  role: 'optional',
  colors: {
    background: 0x8bd3e8,
    fog: 0xa8dce7,
    plotTop: 0xe7cb87,
    plotEdge: 0x4c6574,
    path: 0xeedba7,
    foliage: 0x3f9b62,
    foliageAccent: 0x80bf68,
    trunk: 0x99613b,
    stone: 0xd7d6ca,
    practical: 0xffdc83,
    skyTop: 0x58b9dc,
    skyHorizon: 0xc8eff0,
  },
  surface: { groundRoughness: 0.84, sceneryRoughness: 0.78, stoneRoughness: 0.72 },
  sky: {
    turbidity: 1.8,
    rayleigh: 1.5,
    mieCoefficient: 0.003,
    mieDirectionalG: 0.76,
    elevationDegrees: 47,
    azimuthDegrees: 120,
  },
  lighting: {
    hemisphereSky: 0xf5fdff,
    hemisphereGround: 0x35513a,
    hemisphereIntensity: 0.56,
    sunColor: 0xfff2cf,
    sunIntensity: 3,
    fillColor: 0x93d7df,
    fillIntensity: 0.18,
    exposure: 1,
  },
  fog: { near: 138, far: 300 },
  scenery: { treeRatio: 0.48, hedgeRatio: 0.34, lampRatio: 0.18 },
  effects: { bloomCandidate: false, outlineCandidate: true },
});

const afterglow = freezeTheme({
  id: 'afterglow',
  label: 'Afterglow',
  role: 'optional',
  colors: {
    background: 0x456888,
    fog: 0x486c8b,
    plotTop: 0x3d4947,
    plotEdge: 0x18283b,
    path: 0x665f57,
    foliage: 0x315b4b,
    foliageAccent: 0x4f7662,
    trunk: 0x493b38,
    stone: 0x6d7278,
    practical: 0xffc56f,
    skyTop: 0x172842,
    skyHorizon: 0x547da0,
  },
  surface: { groundRoughness: 0.9, sceneryRoughness: 0.88, stoneRoughness: 0.8 },
  sky: {
    turbidity: 5,
    rayleigh: 0.58,
    mieCoefficient: 0.007,
    mieDirectionalG: 0.84,
    elevationDegrees: 7,
    azimuthDegrees: 132,
  },
  lighting: {
    hemisphereSky: 0x91add0,
    hemisphereGround: 0x25313b,
    hemisphereIntensity: 0.54,
    sunColor: 0xc4d3e8,
    sunIntensity: 1.85,
    fillColor: 0x5d7fb5,
    fillIntensity: 0.46,
    exposure: 1.08,
  },
  fog: { near: 112, far: 245 },
  scenery: { treeRatio: 0.48, hedgeRatio: 0.24, lampRatio: 0.28 },
  effects: { bloomCandidate: true, outlineCandidate: true },
});

export const DEFAULT_ENVIRONMENT_THEME_ID: EnvironmentThemeId = 'park_model';

export const ENVIRONMENT_THEMES: Readonly<Record<EnvironmentThemeId, EnvironmentThemeDefinition>> =
  Object.freeze({
    park_model: parkModel,
    toyworks,
    afterglow,
  });

export function getEnvironmentTheme(id: EnvironmentThemeId): EnvironmentThemeDefinition {
  return ENVIRONMENT_THEMES[id];
}
